/**
 * Roadmap list view: expandable three-level list (track → topic → snippet) replacing the SVG graph.
 * Pure render layer — data fetching, lock/unlock, and study/test handlers come from RoadmapView.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as NavBackIcon } from '../../Icons/nav-back.svg';
import { ReactComponent as NavForwardIcon } from '../../Icons/nav-forward.svg';
import { ReactComponent as SearchIcon } from '../../Icons/search.svg';
import { FaRegEye } from 'react-icons/fa';
import IconRenderer from '../ui/IconRenderer';
import * as roadmapService from '../../services/roadmapService';
import { isRoadmapFreePublicTrack, userCanAccessRoadmapTrack } from '../../utils/roadmapSubscription';
import {
  formatCatalogTrackDisplayTitle,
  formatLanguageDisplay,
  getTrackLanguageKey
} from '../../utils/roadmapDisplay';
import { getLanguageBrandColor } from '../../utils/languageBrandColor';
import { isCardMastered } from '../SRS/algorithm';
import {
  formatSnippetMasteryCountAria,
  formatSnippetMasteryCountTitle
} from '../../constants/snippetMasteryCopy';
import MasteryBrainIcon from '../Achievements/MasteryBrainIcon';
import { computeTrackSnippetStartedNotDue } from '../../utils/roadmapPracticeStartedSession';
import CookbookLearnMoreMenu from './CookbookLearnMoreMenu';
import './RoadmapList.css';

/** Maximum snippet level (display cap; mastery is SRS-gated via {@link isCardMastered}). */
const MAX_SNIPPET_LEVEL = 25;

/** Neutral in-progress fill for track/topic progress accents (green reserved for mastered/complete). */
const ACCENT_ACTIVE = '#94a3b8';
const ACCENT_COMPLETE = '#1D9E75';
const ACCENT_PARTIAL = '#BA7517';
/** In-progress / current-node accent for topic rows (warm yellow, signals "you're here"). */
const ACCENT_IN_PROGRESS = '#E0B341';

/** Collapsed track rows shown per page in the Learn split-view right panel. */
const TRACKS_PER_PAGE = 8;

function matchesTrackQuery(track, language, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const rawTitle = String(track.title || '').toLowerCase();
  const displayTitle = formatCatalogTrackDisplayTitle(language, track.title, track).toLowerCase();
  return rawTitle.includes(q) || displayTitle.includes(q);
}

function RoadmapListTrackHeaderIcon() {
  return (
    <div className="roadmap-list__page-header-icon roadmap-list__page-header-icon--track" aria-hidden>
      <RoadmapIcon />
    </div>
  );
}

function RoadmapListScopeIcon({ scopeKind, roleIconClass, language }) {
  const wrapperClass = [
    'roadmap-list__page-header-icon',
    scopeKind === 'role'
      ? 'roadmap-list__page-header-icon--role'
      : 'roadmap-list__page-header-icon--language'
  ].join(' ');

  return (
    <div className={wrapperClass} aria-hidden>
      {scopeKind === 'role' ? (
        roleIconClass ? (
          <i
            className={`ti ${roleIconClass} roadmap-list__page-header-role-icon`}
            aria-hidden="true"
          />
        ) : (
          <span className="roadmap-list__page-header-role-icon-fallback" aria-hidden>
            <i className="ti ti-user roadmap-list__page-header-role-icon" aria-hidden="true" />
          </span>
        )
      ) : language ? (
        <IconRenderer
          name={String(language).toLowerCase()}
          type="language"
          size="medium"
          className="roadmap-list__page-header-language-icon-svg"
        />
      ) : null}
    </div>
  );
}

function snippetReviewCount(stats) {
  if (!stats || typeof stats !== 'object') return 0;
  return Number(stats.totalReviews ?? stats.totalAttempts ?? 0) || 0;
}

/**
 * Map loaded snippet stats to the displayed level (cookbook card level from Study mode).
 */
function snippetLevelFromStats(stats) {
  if (!stats || typeof stats !== 'object') return 1;
  const raw = stats.level;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.min(MAX_SNIPPET_LEVEL, Math.max(1, Math.round(raw)));
  }
  return 1;
}

function isSnippetMastered(stats) {
  if (!stats || typeof stats !== 'object') return false;
  if (typeof stats.isMastered === 'boolean') return stats.isMastered;
  return isCardMastered({
    mastery: stats.mastery,
    totalReviews: snippetReviewCount(stats),
    repetitions: stats.repetitions,
    interval: stats.interval,
    firstReviewed: stats.firstReviewed,
    lastReviewed: stats.lastReviewed
  });
}

function isSnippetStarted(stats) {
  if (!stats || typeof stats !== 'object') return false;
  if (snippetReviewCount(stats) > 0 || isSnippetMastered(stats)) return true;
  // Card leveling can land before/without SRS rows (or after progress remaps).
  if (snippetLevelFromStats(stats) > 1) return true;
  return Number(stats.current_exp) > 0;
}

export { isSnippetMastered, isSnippetStarted };

/**
 * Total + mastered snippets for a track (de-duped across cookbooks). Drives the track
 * progress bar and completion styling in the list view.
 */
export function computeTrackSnippetMastery(nodes, snippetStatsById) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return { total: 0, mastered: 0 };
  }
  const seen = new Set();
  let total = 0;
  let mastered = 0;
  for (const node of nodes) {
    const ids = node?.snippetIds;
    if (!Array.isArray(ids)) continue;
    for (const id of ids) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      total += 1;
      if (isSnippetMastered(snippetStatsById?.[id])) mastered += 1;
    }
  }
  return { total, mastered };
}

/** Total + started snippets for a track (de-duped across cookbooks). */
export function computeTrackSnippetStarted(nodes, snippetStatsById) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return { total: 0, started: 0 };
  }
  const seen = new Set();
  let total = 0;
  let started = 0;
  for (const node of nodes) {
    const ids = node?.snippetIds;
    if (!Array.isArray(ids)) continue;
    for (const id of ids) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      total += 1;
      if (isSnippetStarted(snippetStatsById?.[id])) started += 1;
    }
  }
  return { total, started };
}

/** Due snippet count for a track (de-duped across cookbooks). */
export function computeTrackDueCount(nodes, dueSnippetIds) {
  if (!(dueSnippetIds instanceof Set) || dueSnippetIds.size === 0) return 0;
  if (!Array.isArray(nodes) || nodes.length === 0) return 0;
  const seen = new Set();
  let count = 0;
  for (const node of nodes) {
    const ids = node?.snippetIds;
    if (!Array.isArray(ids)) continue;
    for (const id of ids) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      if (dueSnippetIds.has(id)) count += 1;
    }
  }
  return count;
}

/** Due snippets in cookbook order for one track — drives the track review list. */
export function computeTrackDueSnippetEntries(track, nodes, dueSnippetIds) {
  if (!track || !(dueSnippetIds instanceof Set) || dueSnippetIds.size === 0) return [];
  if (!Array.isArray(nodes) || nodes.length === 0) return [];
  const entries = [];
  const seen = new Set();
  for (const node of nodes) {
    for (const id of node.snippetIds || []) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      if (dueSnippetIds.has(id)) {
        entries.push({ snippetId: id, track, node });
      }
    }
  }
  return entries;
}

/** Group due snippet entries by cookbook, preserving track cookbook order. */
export function groupTrackDueSnippetEntries(orderedNodes, entries) {
  if (!Array.isArray(entries) || entries.length === 0) return [];
  const byNodeId = new Map();
  for (const entry of entries) {
    const nodeId = entry.node?.id;
    if (!nodeId) continue;
    if (!byNodeId.has(nodeId)) {
      byNodeId.set(nodeId, { node: entry.node, track: entry.track, entries: [] });
    }
    byNodeId.get(nodeId).entries.push(entry);
  }
  const groups = [];
  if (Array.isArray(orderedNodes)) {
    for (const node of orderedNodes) {
      const group = byNodeId.get(node.id);
      if (group?.entries.length) groups.push(group);
    }
  } else {
    groups.push(...byNodeId.values());
  }
  return groups;
}

function getCookbookGroupKey(group) {
  const trackId = group.track?.trackId;
  const nodeId = group.node?.id;
  return trackId && nodeId ? `${trackId}:${nodeId}` : nodeId || '';
}

/** Group due snippet entries by cookbook across all tracks in roadmap order. */
export function groupLanguageDueSnippetEntries(tracks, nodesByTrackId, entries) {
  if (!Array.isArray(entries) || entries.length === 0) return [];
  if (!Array.isArray(tracks) || tracks.length === 0) {
    return groupTrackDueSnippetEntries(null, entries);
  }

  const entriesByTrackId = new Map();
  for (const entry of entries) {
    const trackId = entry.track?.trackId;
    if (!trackId) continue;
    if (!entriesByTrackId.has(trackId)) entriesByTrackId.set(trackId, []);
    entriesByTrackId.get(trackId).push(entry);
  }

  const groups = [];
  for (const track of tracks) {
    const trackEntries = entriesByTrackId.get(track.trackId);
    if (!trackEntries?.length) continue;
    const nodes = nodesByTrackId?.get?.(track.trackId);
    const trackGroups = groupTrackDueSnippetEntries(nodes, trackEntries);
    groups.push(...trackGroups);
  }
  return groups;
}

function ChevronIcon({ open }) {
  return (
    <svg
      className={`roadmap-list__chevron-icon ${open ? 'roadmap-list__chevron-icon--open' : ''}`.trim()}
      width="10"
      height="10"
      viewBox="0 0 10 10"
      aria-hidden
    >
      <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Book glyph used to mark topic rows (matches the Library "Cookbooks" icon `Icons/book.svg`). */
function CookbookIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      aria-hidden
      className="roadmap-list__topic-icon"
    >
      <path
        fill="currentColor"
        d="M3 18.5V5C3 3.34315 4.34315 2 6 2H20C20.5523 2 21 2.44772 21 3V21C21 21.5523 20.5523 22 20 22H6.5C4.567 22 3 20.433 3 18.5ZM19 20V17H6.5C5.67157 17 5 17.6716 5 18.5C5 19.3284 5.67157 20 6.5 20H19ZM5 15.3368C5.45463 15.1208 5.9632 15 6.5 15H19V4H6C5.44772 4 5 4.44772 5 5V15.3368Z"
      />
    </svg>
  );
}

/** Three-node graph glyph used to mark "track" labels (matches `Icons/roadmap-node-icon.svg`). */
function RoadmapIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="roadmap-list__roadmap-icon"
    >
      <path d="M5 16 L12 8 L19 16" />
      <circle cx="5" cy="16" r="3.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="8" r="3.5" fill="currentColor" stroke="none" />
      <circle cx="19" cy="16" r="3.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Code-brackets glyph used to mark snippet rows (matches the Library `<>` icon). */
function SnippetIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      className="roadmap-list__snippet-icon"
    >
      <path
        fill="currentColor"
        d="m8 18l-6-6l6-6l1.425 1.425l-4.6 4.6L9.4 16.6zm8 0l-1.425-1.425l4.6-4.6L14.6 7.4L16 6l6 6z"
      />
    </svg>
  );
}

/** Refresh-arrow glyph used to mark "due for review" counts (matches `Icons/refresh-icon.svg`). */
function ReviewIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      aria-hidden
      className="roadmap-list__review-icon"
    >
      <path
        fill="currentColor"
        d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z"
      />
    </svg>
  );
}

/** Hover label when the tracks scope panel chrome opens review. */
const SHOW_REVIEW_HOVER_LABEL = 'Show Review';

function RoadmapListScopeNav({
  backActive = false,
  backInteractive = false,
  onBackClick,
  backAriaLabel = 'Back',
  backEmphasized = false,
  forwardActive = false,
  forwardInteractive = false,
  onForwardClick,
  forwardAriaLabel = 'Forward',
  forwardEmphasized = false,
  indicatorHint = false,
  indicatorAriaLabel,
  children
}) {
  const backClassName = [
    'roadmap-list__scope-nav-btn',
    'roadmap-list__scope-nav-btn--back',
    backActive ? 'roadmap-list__scope-nav-btn--active' : 'roadmap-list__scope-nav-btn--inactive',
    backInteractive ? 'roadmap-list__scope-nav-btn--interactive' : '',
    backEmphasized ? 'roadmap-list__scope-nav-btn--emphasized' : ''
  ]
    .filter(Boolean)
    .join(' ');

  const forwardClassName = [
    'roadmap-list__scope-nav-btn',
    'roadmap-list__scope-nav-btn--forward',
    forwardActive ? 'roadmap-list__scope-nav-btn--active' : 'roadmap-list__scope-nav-btn--inactive',
    forwardInteractive ? 'roadmap-list__scope-nav-btn--interactive' : '',
    forwardEmphasized ? 'roadmap-list__scope-nav-btn--emphasized' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="roadmap-list__scope-nav">
      {backInteractive ? (
        <button
          type="button"
          className={backClassName}
          onClick={(e) => {
            e.stopPropagation();
            onBackClick?.();
          }}
          aria-label={backAriaLabel}
        >
          <NavBackIcon
            className="roadmap-list__scope-nav-btn-icon"
            aria-hidden
            focusable="false"
          />
        </button>
      ) : (
        <span className={backClassName} aria-hidden>
          <NavBackIcon
            className="roadmap-list__scope-nav-btn-icon"
            aria-hidden
            focusable="false"
          />
        </span>
      )}
      <div
        className={[
          'roadmap-list__scope-nav-display-indicator',
          indicatorHint ? 'roadmap-list__scope-nav-display-indicator--hint' : ''
        ]
          .filter(Boolean)
          .join(' ')}
        role="status"
        aria-live="polite"
        aria-label={indicatorAriaLabel}
      >
        {children}
      </div>
      {forwardInteractive ? (
        <button
          type="button"
          className={forwardClassName}
          onClick={(e) => {
            e.stopPropagation();
            onForwardClick?.();
          }}
          aria-label={forwardAriaLabel}
        >
          <NavForwardIcon
            className="roadmap-list__scope-nav-btn-icon"
            aria-hidden
            focusable="false"
          />
        </button>
      ) : (
        <span className={forwardClassName} aria-hidden>
          <NavForwardIcon
            className="roadmap-list__scope-nav-btn-icon"
            aria-hidden
            focusable="false"
          />
        </span>
      )}
    </div>
  );
}

/**
 * Mini progress ring for the studied count — arc fill reflects snippets started / total.
 */
function StudiedProgressRing({ started, total, className }) {
  const safeTotal = Math.max(1, Number(total) || 1);
  const safeStarted = Math.max(0, Math.min(safeTotal, Number(started) || 0));
  const progress = safeStarted / safeTotal;
  const size = 12;
  const stroke = 1.75;
  const radius = (size - stroke) / 2 - 0.25;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
      className={className}
    >
      <circle
        className="roadmap-list__topic-studied-ring-track"
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
      />
      <circle
        className="roadmap-list__topic-studied-ring-fill"
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        transform={`rotate(-90 ${cx} ${cy})`}
      />
    </svg>
  );
}

/**
 * Mini progress ring for mastered count — check motif always visible; green disc when complete.
 */
function MasteredProgressRing({ mastered, total, className }) {
  const safeTotal = Math.max(1, Number(total) || 1);
  const safeMastered = Math.max(0, Math.min(safeTotal, Number(mastered) || 0));
  const isComplete = safeMastered >= safeTotal;
  const hasProgress = safeMastered > 0;
  const progress = safeMastered / safeTotal;
  const size = 12;
  const stroke = 1.75;
  const radius = (size - stroke) / 2 - 0.25;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
      className={[
        className,
        isComplete ? 'roadmap-list__topic-mastered-ring--complete' : '',
        hasProgress && !isComplete ? 'roadmap-list__topic-mastered-ring--active' : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {isComplete ? (
        <circle
          className="roadmap-list__topic-mastered-ring-badge"
          cx={cx}
          cy={cy}
          r={radius + 0.2}
        />
      ) : (
        <>
          <circle
            className="roadmap-list__topic-mastered-ring-track"
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
          />
          {hasProgress ? (
            <circle
              className="roadmap-list__topic-mastered-ring-fill"
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              transform={`rotate(-90 ${cx} ${cy})`}
            />
          ) : null}
        </>
      )}
      <path
        className="roadmap-list__topic-mastered-ring-check"
        d="M3.35 6.05 L5.05 7.75 L8.65 4.15"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Study glyph: graduation cap (matches `Icons/graduation-cap-icon.svg`).
 */
function StudyActionIcon({ className }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className={className}
    >
      <path d="M12 3L1 9L12 15L21 10.2V17H23V9L12 3Z" fill="currentColor" />
      <path d="M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z" fill="currentColor" />
    </svg>
  );
}

function SnippetRow({
  snippet,
  stats,
  onStudy,
  appearance = 'default'
}) {
  const started = appearance === 'due' || isSnippetStarted(stats);
  const mastered = isSnippetMastered(stats);
  const isClickable = typeof onStudy === 'function' && snippet?.id;
  const displayName = snippet.title || snippet.name || 'Snippet';

  const handleClick = (e) => {
    if (!isClickable) return;
    e.stopPropagation();
    onStudy(snippet.id);
  };

  const handleKeyDown = (e) => {
    if (!isClickable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onStudy(snippet.id);
    }
  };

  return (
    <div
      className={[
        'roadmap-list__snippet-row',
        started ? 'roadmap-list__snippet-row--started' : 'roadmap-list__snippet-row--unstarted',
        mastered ? 'roadmap-list__snippet-row--mastered' : '',
        isClickable ? 'roadmap-list__snippet-row--clickable' : ''
      ]
        .filter(Boolean)
        .join(' ')}
      role={isClickable ? 'button' : 'listitem'}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? `Study ${displayName}` : undefined}
      onClick={isClickable ? handleClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
    >
      <span className="roadmap-list__snippet-name-wrap">
        <SnippetIcon />
        <span className="roadmap-list__snippet-name" title={displayName}>
          {displayName}
        </span>
      </span>
    </div>
  );
}

function TopicRow({
  index,
  node,
  track,
  isCompleted,
  isCurrent,
  lastAttempt,
  snippetStatsById,
  onStudySnippet,
  onStudyNode,
  onStartNode,
  onContinueNode,
  loadingNodeAction,
  onPreviewCookbook,
  studyLocked = false,
  defaultExpanded = false,
  rowRef = null
}) {
  const [open, setOpen] = useState(defaultExpanded);
  /** Per-topic snippet name cache (lazy fetch). Titles only — no code. */
  const [snippetsById, setSnippetsById] = useState(null);
  const [loadingSnippets, setLoadingSnippets] = useState(false);

  /** Dedupe legacy colliding IDs so expand list / counters stay accurate. */
  const snippetIds = useMemo(
    () => roadmapService.uniqueSnippetIdsInOrder(node.snippetIds || []),
    [node.snippetIds]
  );

  const startedCount = useMemo(
    () => snippetIds.filter((id) => isSnippetStarted(snippetStatsById[id])).length,
    [snippetIds, snippetStatsById]
  );

  const hasUnstarted = useMemo(
    () => snippetIds.some((id) => !isSnippetStarted(snippetStatsById[id])),
    [snippetIds, snippetStatsById]
  );

  const masteredCount = useMemo(
    () => snippetIds.filter((id) => isSnippetMastered(snippetStatsById[id])).length,
    [snippetIds, snippetStatsById]
  );

  const total = snippetIds.length;

  /**
   * Topic progress dot — mastery + activity (SM2/review), not separate test mode.
   */
  const hasPassedLastAttempt = Boolean(lastAttempt?.passed);
  const testPassedForState = isCompleted || hasPassedLastAttempt;
  const allMasteredForState = total > 0 && masteredCount === total;
  const state =
    testPassedForState && allMasteredForState
      ? 'complete'
      : testPassedForState || isCurrent || startedCount > 0
        ? 'active'
        : 'none';
  const dotColor =
    state === 'complete' ? ACCENT_COMPLETE : state === 'active' ? ACCENT_IN_PROGRESS : 'var(--color-text-tertiary)';

  useEffect(() => {
    if (!open || snippetsById || snippetIds.length === 0) return;
    let cancelled = false;
    setLoadingSnippets(true);
    (async () => {
      try {
        const list = await roadmapService.loadSnippetsByIds(snippetIds);
        if (cancelled) return;
        const map = {};
        // Keep titles only in the expand list — never retain code for copy-able preview.
        for (const s of list) {
          map[s.id] = {
            id: s.id,
            title: s.title || s.name || 'Snippet',
            name: s.name || s.title || 'Snippet'
          };
        }
        setSnippetsById(map);
      } catch (_) {
        if (!cancelled) setSnippetsById({});
      } finally {
        if (!cancelled) setLoadingSnippets(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, snippetIds, snippetsById]);

  const toggleOpen = () => setOpen((o) => !o);

  useEffect(() => {
    if (defaultExpanded) setOpen(true);
  }, [defaultExpanded, node.id]);

  useEffect(() => {
    if (!defaultExpanded || !rowRef) return;
    rowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [defaultExpanded, rowRef, node.id]);

  const studyLoading =
    loadingNodeAction?.nodeId === node.id && loadingNodeAction?.kind === 'study';
  const startLoading =
    loadingNodeAction?.nodeId === node.id && loadingNodeAction?.kind === 'start';
  const continueLoading =
    loadingNodeAction?.nodeId === node.id && loadingNodeAction?.kind === 'continue';
  const allMastered = total > 0 && masteredCount === total;

  const studiedDisabled = total === 0 || studyLoading || !onStudyNode;
  const continueDisabled =
    total === 0 || startedCount === 0 || allMastered || continueLoading || !onContinueNode;
  // Pro browse: Unlock stays available so free users can open subscription.
  const startDisabled = total === 0 || startLoading || !onStartNode;

  const studiedCountVariant =
    startedCount === 0
      ? 'roadmap-list__topic-stat-label--none'
      : startedCount === total
        ? 'roadmap-list__topic-stat-label--complete'
        : 'roadmap-list__topic-stat-label--active';
  const masteredCountVariant = allMastered
    ? 'roadmap-list__topic-stat-label--complete'
    : masteredCount > 0
      ? 'roadmap-list__topic-stat-label--active'
      : 'roadmap-list__topic-stat-label--none';

  const stopRowToggle = (e) => e.stopPropagation();

  const handleStudiedClick = (e) => {
    e.stopPropagation();
    if (studiedDisabled) return;
    onStudyNode(track, node);
  };

  const handleContinueClick = (e) => {
    e.stopPropagation();
    if (continueDisabled) return;
    onContinueNode(track, node);
  };

  const handleStartClick = (e) => {
    e.stopPropagation();
    if (startDisabled) return;
    onStartNode(track, node);
  };

  return (
    <>
      <div
        ref={rowRef}
        className={[
          'roadmap-list__topic-row',
          open ? 'roadmap-list__topic-row--open' : ''
        ]
          .filter(Boolean)
          .join(' ')}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`Cookbook ${index + 1}: ${node.title}`}
        onClick={toggleOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleOpen();
          }
        }}
      >
        <div className="roadmap-list__topic-row-left">
          <div className="roadmap-list__topic-title-block">
            <div className="roadmap-list__topic-title-line">
              <span className="roadmap-list__topic-dot" style={{ background: dotColor }} aria-hidden />
              <CookbookIcon />
              <span className="roadmap-list__topic-name">{node.title}</span>
            </div>
            {total > 0 ? (
              <span className="roadmap-list__topic-meta">
                {onStudyNode ? (
                  <button
                    type="button"
                    className={[
                      'roadmap-list__topic-stat-label',
                      'roadmap-list__topic-stat-label--studied',
                      studiedCountVariant
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={handleStudiedClick}
                    onKeyDown={stopRowToggle}
                    disabled={studiedDisabled}
                    title={`${startedCount}/${total} snippets studied · Open cookbook`}
                    aria-label={`Studied ${startedCount} of ${total} snippets in ${node.title} · Open cookbook`}
                  >
                    <StudiedProgressRing
                      started={startedCount}
                      total={total}
                      className="roadmap-list__topic-studied-ring"
                    />
                    <span className="roadmap-list__topic-stat-label-name">Started</span>
                    <span className="roadmap-list__topic-stat-label-count">
                      {studyLoading ? '…' : `${startedCount}/${total}`}
                    </span>
                  </button>
                ) : null}
                {onContinueNode ? (
                  <button
                    type="button"
                    className={[
                      'roadmap-list__topic-stat-label',
                      'roadmap-list__topic-stat-label--mastered',
                      masteredCountVariant
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={handleContinueClick}
                    onKeyDown={stopRowToggle}
                    disabled={continueDisabled}
                    title={formatSnippetMasteryCountTitle(masteredCount, total, ' · Continue learning')}
                    aria-label={formatSnippetMasteryCountAria(masteredCount, total, ` in ${node.title} · Continue learning`)}
                  >
                    <MasteredProgressRing
                      mastered={masteredCount}
                      total={total}
                      className="roadmap-list__topic-mastered-ring"
                    />
                    <span className="roadmap-list__topic-stat-label-name">Mastered</span>
                    <span className="roadmap-list__topic-stat-label-count">
                      {continueLoading ? '…' : `${masteredCount}/${total}`}
                    </span>
                  </button>
                ) : null}
              </span>
            ) : null}
          </div>
        </div>
        <div className="roadmap-list__topic-row-right">
          {total > 0 ? (
            <CookbookLearnMoreMenu
              track={track}
              topicTitle={node.title}
              onPointerEventStop={stopRowToggle}
            />
          ) : null}
          {total > 0 && onPreviewCookbook ? (
            <button
              type="button"
              className="roadmap-list__topic-action roadmap-list__topic-action--study roadmap-list__topic-action--icon-only"
              onClick={(e) => {
                e.stopPropagation();
                onPreviewCookbook(index);
              }}
              onKeyDown={stopRowToggle}
              title={`Preview snippets in ${node.title}`}
              aria-label={`Preview snippets in ${node.title}`}
            >
              <FaRegEye className="roadmap-list__topic-action-icon-svg" aria-hidden />
            </button>
          ) : null}
          {total > 0 && onStartNode ? (
            <button
              type="button"
              className={[
                'roadmap-list__topic-action',
                'roadmap-list__topic-action--study',
                allMastered ? 'roadmap-list__topic-action--study-complete' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={handleStartClick}
              onKeyDown={stopRowToggle}
              disabled={startDisabled}
              title={
                studyLocked
                  ? `Subscribe to unlock snippets in ${node.title}`
                  : hasUnstarted
                    ? `Start next unstudied snippet in ${node.title}`
                    : `Open cookbook to study ${node.title}`
              }
              aria-label={
                studyLocked
                  ? `Subscribe to unlock snippets in ${node.title}`
                  : hasUnstarted
                    ? `Start next unstudied snippet in ${node.title}`
                    : `Open cookbook to study ${node.title}`
              }
            >
              <span className="roadmap-list__topic-action-label">
                {startLoading ? '…' : studyLocked ? 'Unlock' : 'Start'}
              </span>
            </button>
          ) : null}
          <ChevronIcon open={open} />
        </div>
      </div>
      {open ? (
        <div className="roadmap-list__snippet-drawer" role="list">
          {total === 0 ? (
            <div className="roadmap-list__snippet-empty">No snippets in this cookbook.</div>
          ) : loadingSnippets && !snippetsById ? (
            <div className="roadmap-list__snippet-empty">Loading snippets…</div>
          ) : (
            snippetIds.map((sid, i) => {
              const meta = (snippetsById && snippetsById[sid]) || { id: sid, title: 'Snippet' };
              return (
                <SnippetRow
                  key={`${sid}-${i}`}
                  snippet={meta}
                  stats={snippetStatsById[sid]}
                  onStudy={onStudySnippet ? () => onStudySnippet(node, sid) : undefined}
                />
              );
            })
          )}
        </div>
      ) : null}
    </>
  );
}

function CookbookSnippetPreviewModal({
  track,
  cookbook,
  cookbookIndex,
  snippetIndex,
  snippetStatsById,
  onSnippetIndexChange,
  onClose,
  onStudySnippet
}) {
  const [snippets, setSnippets] = useState([]);
  const [loadingSnippets, setLoadingSnippets] = useState(false);
  const [footerActionHovered, setFooterActionHovered] = useState(false);

  const snippetIds = cookbook?.snippetIds || [];
  const safeSnippetIndex = Math.min(
    Math.max(0, snippetIndex),
    Math.max(0, snippetIds.length - 1)
  );
  const currentSnippetId = snippetIds[safeSnippetIndex] || null;
  const currentSnippet = snippets.find((s) => s.id === currentSnippetId) || null;
  const currentStats = currentSnippetId ? snippetStatsById[currentSnippetId] : null;

  useEffect(() => {
    if (!cookbook?.snippetIds?.length) {
      setSnippets([]);
      setLoadingSnippets(false);
      return;
    }

    let cancelled = false;
    setLoadingSnippets(true);
    (async () => {
      try {
        const list = await roadmapService.loadSnippetsByIds(cookbook.snippetIds);
        if (!cancelled) setSnippets(list);
      } catch (err) {
        console.error('CookbookSnippetPreviewModal: load snippets failed', err);
        if (!cancelled) setSnippets([]);
      } finally {
        if (!cancelled) setLoadingSnippets(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cookbook?.id, cookbook?.snippetIds?.join('|')]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    setFooterActionHovered(false);
  }, [safeSnippetIndex, currentSnippetId]);

  const canPrevSnippet = safeSnippetIndex > 0;
  const canNextSnippet = safeSnippetIndex < snippetIds.length - 1;
  const cookbookTitle = cookbook?.title || 'Cookbook';
  const snippetTitle = currentSnippet?.title || currentSnippet?.name || 'Snippet';
  const snippetStarted = isSnippetStarted(currentStats);
  const snippetMastered = isSnippetMastered(currentStats);
  const footerActionLabel = !snippetStarted
    ? 'Start'
    : footerActionHovered
      ? 'Review'
      : 'Started';

  const handleFooterAction = () => {
    if (!currentSnippetId || typeof onStudySnippet !== 'function') return;
    onStudySnippet(track, cookbook, currentSnippetId);
  };

  return createPortal(
    <div
      className="roadmap-list__cookbook-preview-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Preview snippets in ${cookbookTitle}`}
    >
      <div
        className="roadmap-list__cookbook-preview-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="roadmap-list__cookbook-preview-modal-header">
          <div className="roadmap-list__cookbook-preview-modal-header-left">
            <span className="roadmap-list__pagination-context-track-num">
              {String(cookbookIndex + 1).padStart(2, '0')}
            </span>
            <span className="roadmap-list__pagination-context-sep" aria-hidden>
              ·
            </span>
            <h2 className="roadmap-list__cookbook-preview-modal-title" title={cookbookTitle}>
              {cookbookTitle}
            </h2>
          </div>
          <button
            type="button"
            className="roadmap-list__cookbook-preview-modal-close"
            onClick={onClose}
            aria-label="Close preview"
          >
            ×
          </button>
        </header>

        <div className="roadmap-list__cookbook-preview-modal-body">
          <div className="roadmap-list__track-preview-stack">
            {loadingSnippets ? (
              <p className="roadmap-list__topic-empty">Loading snippet…</p>
            ) : !currentSnippet ? (
              <p className="roadmap-list__topic-empty">No snippet to preview.</p>
            ) : (
              <article
                className={[
                  'roadmap-list__track-card',
                  'roadmap-list__track-preview-card',
                  snippetStarted ? 'roadmap-list__track-preview-card--started' : '',
                  snippetMastered ? 'roadmap-list__track-preview-card--mastered' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="roadmap-list__track-preview-snippet-head">
                  <div className="roadmap-list__topic-title-block">
                    <div className="roadmap-list__topic-title-line">
                      <SnippetIcon />
                      <h3 className="roadmap-list__topic-name">{snippetTitle}</h3>
                    </div>
                  </div>
                </div>
                {currentSnippet.description && currentSnippet.description !== snippetTitle ? (
                  <p className="roadmap-list__track-preview-description">{currentSnippet.description}</p>
                ) : null}
                <div className="roadmap-list__track-preview-code-wrap">
                  {currentSnippet.code ? (
                    <div className="roadmap-list__track-preview-code">
                      <pre>
                        <code>{currentSnippet.code}</code>
                      </pre>
                    </div>
                  ) : (
                    <p className="roadmap-list__snippet-empty">No preview code available.</p>
                  )}
                </div>
              </article>
            )}
          </div>
        </div>

        <nav
          className="roadmap-list__cookbook-preview-modal-footer"
          aria-label="Snippet navigation"
        >
          <div className="roadmap-list__cookbook-preview-modal-footer-status">
            <span
              className="roadmap-list__pagination-count"
              aria-label={`Snippet ${safeSnippetIndex + 1} of ${snippetIds.length || 0}`}
            >
              <strong className="roadmap-list__pagination-count-current">
                {snippetIds.length > 0 ? safeSnippetIndex + 1 : 0}
              </strong>
              <span className="roadmap-list__pagination-count-separator" aria-hidden>
                /
              </span>
              <span className="roadmap-list__pagination-count-total">{snippetIds.length}</span>
            </span>
            {snippetIds.length > 1 ? (
              <div className="roadmap-list__pagination-dots" role="tablist" aria-label="Snippet position">
                {snippetIds.map((sid, index) => {
                  const dotStats = snippetStatsById[sid];
                  const dotStarted = isSnippetStarted(dotStats);
                  const dotMastered = isSnippetMastered(dotStats);
                  const isActive = index === safeSnippetIndex;
                  return (
                    <button
                      key={`${cookbook?.id || 'cb'}-snippet-dot-${index}`}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-label={`Snippet ${index + 1} of ${snippetIds.length}${dotMastered ? ', mastered' : dotStarted ? ', started' : ''}`}
                      className={[
                        'roadmap-list__pagination-dot',
                        isActive ? 'roadmap-list__pagination-dot--active' : '',
                        dotMastered ? 'roadmap-list__pagination-dot--mastered' : '',
                        dotStarted && !dotMastered ? 'roadmap-list__pagination-dot--started' : ''
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => onSnippetIndexChange(index)}
                    />
                  );
                })}
              </div>
            ) : null}
          </div>

          {typeof onStudySnippet === 'function' && cookbook && currentSnippetId ? (
            <div className="roadmap-list__cookbook-preview-modal-footer-start">
              <button
                type="button"
                className={[
                  'roadmap-list__topic-action',
                  'roadmap-list__topic-action--study',
                  'roadmap-list__cookbook-preview-modal-footer-start-btn',
                  snippetStarted ? 'roadmap-list__cookbook-preview-modal-footer-start-btn--started' : '',
                  snippetMastered ? 'roadmap-list__topic-action--study-complete' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={handleFooterAction}
                onMouseEnter={() => setFooterActionHovered(true)}
                onMouseLeave={() => setFooterActionHovered(false)}
                onFocus={() => setFooterActionHovered(true)}
                onBlur={() => setFooterActionHovered(false)}
                aria-label={
                  snippetStarted
                    ? `Review ${snippetTitle}`
                    : `Start ${snippetTitle}`
                }
              >
                <span className="roadmap-list__topic-action-label">{footerActionLabel}</span>
              </button>
            </div>
          ) : (
            <div className="roadmap-list__cookbook-preview-modal-footer-start" aria-hidden />
          )}

          <div className="roadmap-list__cookbook-preview-modal-footer-actions">
            <button
              type="button"
              className="roadmap-list__pagination-button roadmap-list__pagination-button--prev"
              onClick={() => onSnippetIndexChange(safeSnippetIndex - 1)}
              disabled={!canPrevSnippet}
              aria-label="Previous snippet"
            >
              <NavBackIcon className="roadmap-list__pagination-button-icon" aria-hidden focusable="false" />
            </button>
            <button
              type="button"
              className="roadmap-list__pagination-button roadmap-list__pagination-button--next"
              onClick={() => onSnippetIndexChange(safeSnippetIndex + 1)}
              disabled={!canNextSnippet}
              aria-label="Next snippet"
            >
              <NavForwardIcon className="roadmap-list__pagination-button-icon" aria-hidden focusable="false" />
            </button>
          </div>
        </nav>
      </div>
    </div>,
    document.body
  );
}

function TrackPreviewPanel({
  track,
  cookbooks = [],
  snippetStatsById = {},
  dueSnippetIds = null,
  isLoadingCookbooks = false,
  completedNodeIds = [],
  currentNodeId = null,
  nodeLastAttempts = {},
  canPreviewSnippets = true,
  onStudySnippet,
  onStudyNode,
  onStartNode,
  onContinueNode,
  onPracticeStarted,
  loadingNodeAction = null,
  initialFocusNodeId = null
}) {
  const [previewCookbookIndex, setPreviewCookbookIndex] = useState(null);
  const [previewSnippetIndex, setPreviewSnippetIndex] = useState(0);
  const focusRowRef = useRef(null);

  const topicsForRender = Array.isArray(cookbooks) ? cookbooks : [];
  const completedSet = useMemo(() => new Set(completedNodeIds || []), [completedNodeIds]);

  const handlePreviewCookbook = useCallback((cookbookIndex) => {
    if (!canPreviewSnippets) return;
    setPreviewCookbookIndex(cookbookIndex);
    setPreviewSnippetIndex(0);
  }, [canPreviewSnippets]);

  const handleClosePreview = useCallback(() => {
    setPreviewCookbookIndex(null);
    setPreviewSnippetIndex(0);
  }, []);

  const handlePreviewSnippetChange = useCallback((snippetIndex) => {
    setPreviewSnippetIndex(snippetIndex);
  }, []);

  useEffect(() => {
    setPreviewCookbookIndex(null);
    setPreviewSnippetIndex(0);
  }, [track?.trackId]);

  useEffect(() => {
    if (!canPreviewSnippets) {
      setPreviewCookbookIndex(null);
      setPreviewSnippetIndex(0);
    }
  }, [canPreviewSnippets]);

  const previewCookbook =
    canPreviewSnippets && previewCookbookIndex != null
      ? topicsForRender[previewCookbookIndex] || null
      : null;

  const { total: snippetTotal, mastered: snippetMastered } = useMemo(
    () => computeTrackSnippetMastery(topicsForRender, snippetStatsById),
    [topicsForRender, snippetStatsById]
  );

  const { started: snippetStartedCount } = useMemo(
    () => computeTrackSnippetStarted(topicsForRender, snippetStatsById),
    [topicsForRender, snippetStatsById]
  );

  const { startedNotDue: practiceStartedCount } = useMemo(
    () =>
      computeTrackSnippetStartedNotDue(
        topicsForRender,
        snippetStatsById,
        dueSnippetIds instanceof Set ? dueSnippetIds : new Set()
      ),
    [topicsForRender, snippetStatsById, dueSnippetIds]
  );

  const pct = snippetTotal > 0 ? Math.round((snippetMastered / snippetTotal) * 100) : 0;
  const isComplete = snippetTotal > 0 && pct >= 100;

  const practiceStartedLoading =
    loadingNodeAction?.kind === 'practiceStarted' &&
    String(loadingNodeAction?.trackId) === String(track?.trackId) &&
    !loadingNodeAction?.nodeId;
  const practiceStartedDisabled =
    practiceStartedCount === 0 || practiceStartedLoading || !onPracticeStarted;
  const practiceStartedVariant =
    practiceStartedCount === 0
      ? 'roadmap-list__track-practice-started-btn--none'
      : snippetStartedCount === snippetTotal
        ? 'roadmap-list__track-practice-started-btn--complete'
        : 'roadmap-list__track-practice-started-btn--active';

  const handlePracticeStartedClick = () => {
    if (practiceStartedDisabled) return;
    onPracticeStarted(track);
  };

  return (
    <div className="roadmap-list__track-preview">
      {previewCookbook ? (
        <CookbookSnippetPreviewModal
          track={track}
          cookbook={previewCookbook}
          cookbookIndex={previewCookbookIndex}
          snippetIndex={previewSnippetIndex}
          snippetStatsById={snippetStatsById}
          onSnippetIndexChange={handlePreviewSnippetChange}
          onClose={handleClosePreview}
          onStudySnippet={
            onStudySnippet ? (t, n, sid) => onStudySnippet(track, n, sid) : undefined
          }
        />
      ) : null}

      <div className="roadmap-list__tracks-wrap">
        <div className="roadmap-list__track-expanded roadmap-list__track-preview-expanded">
          {snippetTotal > 0 ? (
            <div className="roadmap-list__track-progress-row">
              <button
                type="button"
                className={[
                  'roadmap-list__track-practice-started-btn',
                  practiceStartedVariant
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={handlePracticeStartedClick}
                disabled={practiceStartedDisabled}
                title={
                  practiceStartedCount > 0
                    ? `${practiceStartedCount} started snippet${practiceStartedCount === 1 ? '' : 's'} ready to practice · Not due today`
                    : snippetStartedCount > 0
                      ? 'All started snippets are due for review today'
                      : 'No snippets started yet'
                }
                aria-label={
                  practiceStartedCount > 0
                    ? `Practice ${practiceStartedCount} started snippet${practiceStartedCount === 1 ? '' : 's'} not due today in this track`
                    : snippetStartedCount > 0
                      ? 'Practice started snippets — all started snippets are due today'
                      : 'Practice started snippets — none started yet'
                }
              >
                <MasteryBrainIcon className="roadmap-list__track-practice-started-btn__icon" />
                <span className="roadmap-list__track-practice-started-btn__count">
                  {practiceStartedLoading ? '…' : practiceStartedCount}
                </span>
              </button>
              <div className="roadmap-list__track-progress-wrap">
                <div className="roadmap-list__track-progress-labels">
                  <span>Track mastery</span>
                  <span>
                    {snippetMastered} / {snippetTotal}
                  </span>
                </div>
                <div className="roadmap-list__track-progress-bar">
                  <div
                    className={[
                      'roadmap-list__track-progress-bar-fill',
                      isComplete ? 'roadmap-list__track-progress-bar-fill--complete' : ''
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={{
                      width: `${pct}%`,
                      backgroundColor:
                        isComplete ? ACCENT_COMPLETE : pct > 0 ? ACCENT_ACTIVE : 'transparent'
                    }}
                  />
                </div>
              </div>
            </div>
          ) : null}

          <div className="roadmap-list__topic-list" role="list">
            {isLoadingCookbooks && topicsForRender.length === 0 ? (
              <div className="roadmap-list__topic-empty">Loading cookbooks…</div>
            ) : topicsForRender.length === 0 ? (
              <div className="roadmap-list__topic-empty">No cookbooks published for this track yet.</div>
            ) : (
              topicsForRender.map((node, index) => (
                <TopicRow
                  key={node.id}
                  index={index}
                  node={node}
                  track={track}
                  isCompleted={completedSet.has(node.id)}
                  isCurrent={currentNodeId === node.id}
                  lastAttempt={nodeLastAttempts?.[node.id] || null}
                  snippetStatsById={snippetStatsById}
                  onStudyNode={onStudyNode}
                  onStartNode={onStartNode}
                  onContinueNode={onContinueNode}
                  loadingNodeAction={loadingNodeAction}
                  onPreviewCookbook={canPreviewSnippets ? handlePreviewCookbook : undefined}
                  studyLocked={!canPreviewSnippets}
                  defaultExpanded={
                    initialFocusNodeId != null && String(node.id) === String(initialFocusNodeId)
                  }
                  rowRef={
                    initialFocusNodeId != null && String(node.id) === String(initialFocusNodeId)
                      ? focusRowRef
                      : null
                  }
                  onStudySnippet={
                    onStudySnippet ? (n, sid) => onStudySnippet(track, n, sid) : undefined
                  }
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TrackRow({
  index,
  track,
  language,
  isAccessible,
  showSequence,
  trackNodes,
  snippetStatsById,
  dueSnippetIds,
  onOpenTrackPreview
}) {
  const topicsForRender = Array.isArray(trackNodes) ? trackNodes : [];

  const { total: snippetTotal, mastered: snippetMastered } = useMemo(
    () => computeTrackSnippetMastery(topicsForRender, snippetStatsById),
    [topicsForRender, snippetStatsById]
  );

  const dueSnippetCount = useMemo(
    () => computeTrackDueCount(topicsForRender, dueSnippetIds),
    [dueSnippetIds, topicsForRender]
  );

  const pct = snippetTotal > 0 ? Math.round((snippetMastered / snippetTotal) * 100) : 0;
  const isActive = snippetMastered > 0 && pct < 100;
  const isComplete = snippetTotal > 0 && pct >= 100;

  const handleHeaderClick = () => {
    // Free users can preview Pro tracks (cookbooks + snippets); study stays gated elsewhere.
    onOpenTrackPreview?.(track);
  };

  const trackTitle = formatCatalogTrackDisplayTitle(language, track.title, track);
  const topicCount = topicsForRender.length;
  const dueLabel =
    dueSnippetCount > 0
      ? `${dueSnippetCount} snippet${dueSnippetCount === 1 ? '' : 's'} due for review`
      : 'No snippets due for review';

  return (
    <div
      className={[
        'roadmap-list__track-card',
        isActive ? 'roadmap-list__track-card--active' : '',
        isComplete ? 'roadmap-list__track-card--complete' : '',
        !isAccessible ? 'roadmap-list__track-card--locked' : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        className="roadmap-list__track-row"
        role="button"
        tabIndex={0}
        aria-label={`Track ${index + 1}: ${trackTitle}${!isAccessible ? ', Pro track, preview available' : ''}`}
        onClick={handleHeaderClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleHeaderClick();
          }
        }}
      >
        <div
          className={[
            'roadmap-list__track-row-left',
            showSequence ? 'roadmap-list__track-row-left--sequenced' : ''
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {showSequence ? (
            <span className="roadmap-list__track-sequence" aria-hidden>
              <span
                className={[
                  'roadmap-list__track-sequence-num',
                  isActive ? 'roadmap-list__track-sequence-num--active' : '',
                  isComplete ? 'roadmap-list__track-sequence-num--complete' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {String(index + 1).padStart(2, '0')}
              </span>
            </span>
          ) : null}
          <div className="roadmap-list__track-line">
            <span className="roadmap-list__track-name">{trackTitle}</span>
            {(() => {
              const description = (track.description || '').trim();
              const primary = description
                ? (
                  <span className="roadmap-list__track-description" title={description}>
                    {description}
                  </span>
                )
                : topicCount > 0
                  ? (() => {
                    const label = `${topicCount} ${topicCount === 1 ? 'cookbook' : 'cookbooks'}`;
                    return (
                      <span
                        className="roadmap-list__track-description roadmap-list__track-description--cookbook-count"
                        title={label}
                      >
                        <CookbookIcon />
                        {label}
                      </span>
                    );
                  })()
                  : null;
              const masteryLabel =
                snippetTotal > 0 ? `${snippetMastered}/${snippetTotal} mastered` : null;
              if (!primary && topicCount === 0) return null;
              return (
                <span className="roadmap-list__track-meta">
                  {primary}
                  {masteryLabel ? (
                    <span
                      className="roadmap-list__track-description roadmap-list__track-description--mastery-count"
                      title={formatSnippetMasteryCountTitle(snippetMastered, snippetTotal)}
                      aria-label={formatSnippetMasteryCountAria(snippetMastered, snippetTotal)}
                    >
                      <MasteryBrainIcon className="roadmap-list__track-description-mastery-icon" />
                      {masteryLabel}
                    </span>
                  ) : null}
                </span>
              );
            })()}
          </div>
        </div>
        <div className="roadmap-list__track-row-right">
          {!isAccessible ? (
            <span
              className="roadmap-list__track-locked-label"
              title="Pro track — preview cookbooks and snippets; subscribe to study"
            >
              Pro
            </span>
          ) : null}
          {topicCount > 0 && dueSnippetCount > 0 ? (
            <span
              className="roadmap-list__track-description roadmap-list__track-description--due roadmap-list__track-description--due-active roadmap-list__track-due-badge"
              title={dueLabel}
            >
              <ReviewIcon />
              {`${dueSnippetCount} due`}
            </span>
          ) : null}
          <NavForwardIcon className="roadmap-list__track-open-icon" aria-hidden />
        </div>
      </div>
    </div>
  );
}

/** Expandable cookbook row on the track review page — lists due snippets per cookbook. */
function LearnReviewCookbookRow({
  node,
  track,
  entries,
  snippetsById,
  snippetStatsById,
  onReviewDueSnippet,
  open,
  onToggle,
  language = null
}) {
  const dueCount = entries.length;
  const firstEntry = entries[0];
  const trackLang = getTrackLanguageKey(track) || language;

  const handleReviewClick = (e) => {
    e.stopPropagation();
    if (!firstEntry || !onReviewDueSnippet) return;
    onReviewDueSnippet(track, node, firstEntry.snippetId);
  };

  const stopRowToggle = (e) => e.stopPropagation();

  return (
    <>
      <div
        className={[
          'roadmap-list__review-menu-cookbook-row',
          open ? 'roadmap-list__review-menu-cookbook-row--open' : ''
        ]
          .filter(Boolean)
          .join(' ')}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`${node.title}, ${dueCount} snippet${dueCount === 1 ? '' : 's'} due for review`}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="roadmap-list__review-menu-cookbook-row-left">
          {trackLang ? (
            <IconRenderer
              name={String(trackLang).toLowerCase()}
              type="language"
              size="small"
              className="roadmap-list__review-menu-cookbook-language-icon"
            />
          ) : (
            <CookbookIcon />
          )}
          <span className="roadmap-list__review-menu-cookbook-name">{node.title}</span>
        </div>
        <div className="roadmap-list__review-menu-cookbook-row-right">
          <span
            className="roadmap-list__review-menu-cookbook-due-count"
            title={`${dueCount} snippet${dueCount === 1 ? '' : 's'} due`}
          >
            <ReviewIcon />
            {dueCount} due
          </span>
          {onReviewDueSnippet ? (
            <button
              type="button"
              className="roadmap-list__review-menu-cookbook-review-btn"
              onClick={handleReviewClick}
              onKeyDown={stopRowToggle}
              aria-label={`Review due snippets in ${node.title}`}
            >
              Review
            </button>
          ) : null}
          <ChevronIcon open={open} />
        </div>
      </div>
      {open ? (
        <div className="roadmap-list__review-menu-cookbook-drawer" role="list">
          {entries.map((entry) => {
            const meta =
              (snippetsById && snippetsById[entry.snippetId]) ||
              { id: entry.snippetId, title: 'Snippet' };
            return (
              <SnippetRow
                key={entry.snippetId}
                snippet={meta}
                stats={snippetStatsById?.[entry.snippetId]}
                appearance="due"
                onStudy={
                  onReviewDueSnippet
                    ? () => onReviewDueSnippet(entry.track, entry.node, entry.snippetId)
                    : undefined
                }
              />
            );
          })}
        </div>
      ) : null}
    </>
  );
}

/** Back face of the scope panel — review hub (SM2 due queue + continue). */
function LearnReviewMenu({
  dueSnippetEntries,
  cookbookGroups = [],
  snippetStatsById,
  isVisible,
  continueLabel,
  continueDisabled,
  onReviewDueSnippet,
  onContinueStudy,
  onBackToTracks,
  backLabel = 'tracks',
  language = null
}) {
  const [expandedCookbookId, setExpandedCookbookId] = useState(null);
  const [snippetsById, setSnippetsById] = useState(null);
  const [loadingDueSnippets, setLoadingDueSnippets] = useState(false);

  const dueSnippetIds = useMemo(
    () => dueSnippetEntries.map((entry) => entry.snippetId),
    [dueSnippetEntries]
  );
  const dueIdsKey = dueSnippetIds.join('|');

  useEffect(() => {
    if (!isVisible) {
      setExpandedCookbookId(null);
    }
  }, [isVisible, dueIdsKey]);

  useEffect(() => {
    if (!isVisible || dueSnippetIds.length === 0) {
      setSnippetsById({});
      setLoadingDueSnippets(false);
      return;
    }
    let cancelled = false;
    setLoadingDueSnippets(true);
    setSnippetsById(null);
    (async () => {
      try {
        const list = await roadmapService.loadSnippetsByIds(dueSnippetIds);
        if (cancelled) return;
        const map = {};
        for (const s of list) map[s.id] = s;
        setSnippetsById(map);
      } catch (_) {
        if (!cancelled) setSnippetsById({});
      } finally {
        if (!cancelled) setLoadingDueSnippets(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isVisible, dueIdsKey]);

  const dueCount = dueSnippetEntries.length;
  const cookbookCount = cookbookGroups.length;

  return (
    <div className="roadmap-list__review-menu">
      <div className="roadmap-list__review-menu-due-section">
        {dueCount === 0 ? (
          <p className="roadmap-list__review-menu-due-empty">Nothing due for review right now.</p>
        ) : loadingDueSnippets && !snippetsById ? (
          <p className="roadmap-list__review-menu-due-empty">Loading snippets…</p>
        ) : cookbookCount > 0 ? (
          <div className="roadmap-list__review-menu-cookbook-list" role="list">
            {cookbookGroups.map((group) => {
              const groupKey = getCookbookGroupKey(group);
              return (
                <LearnReviewCookbookRow
                  key={groupKey}
                  node={group.node}
                  track={group.track}
                  entries={group.entries}
                  snippetsById={snippetsById}
                  snippetStatsById={snippetStatsById}
                  onReviewDueSnippet={onReviewDueSnippet}
                  open={expandedCookbookId === groupKey}
                  onToggle={() =>
                    setExpandedCookbookId((current) =>
                      current === groupKey ? null : groupKey
                    )
                  }
                  language={language}
                />
              );
            })}
          </div>
        ) : (
          <div className="roadmap-list__review-menu-due-list" role="list">
            {dueSnippetEntries.map((entry) => {
              const meta =
                (snippetsById && snippetsById[entry.snippetId]) ||
                { id: entry.snippetId, title: 'Snippet' };
              return (
                <SnippetRow
                  key={entry.snippetId}
                  snippet={meta}
                  stats={snippetStatsById?.[entry.snippetId]}
                  appearance="due"
                  onStudy={
                    onReviewDueSnippet
                      ? () => onReviewDueSnippet(entry.track, entry.node, entry.snippetId)
                      : undefined
                  }
                />
              );
            })}
          </div>
        )}
      </div>
      <div className="roadmap-list__review-menu-footer">
        <button
          type="button"
          className="roadmap-list__review-menu-continue"
          onClick={(e) => {
            e.stopPropagation();
            onContinueStudy?.();
          }}
          disabled={continueDisabled}
          aria-label={
            continueLabel
              ? `Continue study in ${continueLabel}`
              : 'Continue study in current cookbook'
          }
        >
          <StudyActionIcon className="roadmap-list__review-menu-continue-icon" />
          <span className="roadmap-list__review-menu-continue-text">
            <span className="roadmap-list__review-menu-continue-label">Continue</span>
            {continueLabel ? (
              <span className="roadmap-list__review-menu-continue-hint">{continueLabel}</span>
            ) : null}
          </span>
        </button>
        <button
          type="button"
          className="roadmap-list__review-menu-back"
          onClick={(e) => {
            e.stopPropagation();
            onBackToTracks?.();
          }}
        >
          Back to {backLabel}
        </button>
      </div>
    </div>
  );
}

export default function RoadmapList({
  language,
  tracks,
  selectedTrack,
  orderedNodes,
  completedNodeIds,
  currentNodeId,
  nodeScores,
  nodeLastAttempts,
  nodesByTrackId,
  completedNodesByTrackId,
  dueSnippetIds,
  snippetStatsById,
  trackProgress,
  isPremiumUser,
  isLoadingTracks,
  isLoadingNodes,
  onBack,
  onSelectTrack,
  onStudySnippet,
  onStudyNode,
  onStartNode,
  onContinueNode,
  onPracticeStarted,
  loadingNodeAction = null,
  onGoToReview,
  onReviewDueSnippet,
  onContinueStudy,
  continueStudyLabel = null,
  continueStudyDisabled = true,
  footerContext = null,
  scopeHeader = null,
  emptyMessage = null,
  initialOpenTrackPreview = false,
  initialFocusNodeId = null,
  onInitialOpenTrackPreviewConsumed,
  showRolePathToggle = false,
  isPrimaryRolePath = false,
  isSettingRolePath = false,
  onSetAsRolePath
}) {
  const languageDisplay = formatLanguageDisplay(language || '') || '-';
  const scopeTitle = scopeHeader?.title || languageDisplay;
  const scopeRoleIconClass = scopeHeader?.iconClass || null;
  const scopeSubtitle = scopeHeader?.subtitle || null;
  const headerAriaLabel = scopeHeader
    ? `Role progress summary for ${scopeTitle}`
    : `Language progress summary for ${languageDisplay}`;
  const totalTracks = trackProgress?.total ?? 0;
  const completedTracks = trackProgress?.completed ?? 0;

  const renderedTracks = Array.isArray(tracks) ? tracks : [];

  const [showReviewFace, setShowReviewFace] = useState(false);
  const [scopeNavIndicatorLabel, setScopeNavIndicatorLabel] = useState(null);
  const [trackPage, setTrackPage] = useState(0);
  const [trackQuery, setTrackQuery] = useState('');
  const [trackPreviewOpen, setTrackPreviewOpen] = useState(false);
  const [trackPreviewFocusNodeId, setTrackPreviewFocusNodeId] = useState(null);

  const scopeKind = scopeHeader ? 'role' : 'language';
  const canFlipToReview = typeof onGoToReview === 'function';
  const showTrackSearch = scopeKind === 'language';
  const showRolePathControl =
    scopeKind === 'role' && showRolePathToggle && typeof onSetAsRolePath === 'function';

  const filteredTracks = useMemo(() => {
    if (!showTrackSearch || !trackQuery.trim()) return renderedTracks;
    return renderedTracks.filter((track) => matchesTrackQuery(track, language, trackQuery));
  }, [renderedTracks, language, trackQuery, showTrackSearch]);

  const hasActiveTrackSearch = trackQuery.trim().length > 0;
  const showTrackSequence = !hasActiveTrackSearch && renderedTracks.length > 1;

  useEffect(() => {
    setShowReviewFace(false);
    setScopeNavIndicatorLabel(null);
    setTrackQuery('');
    setTrackPreviewOpen(false);
    setTrackPreviewFocusNodeId(null);
  }, [language, scopeHeader?.title]);

  const previewTrackTitle = selectedTrack
    ? formatCatalogTrackDisplayTitle(language, selectedTrack.title, selectedTrack)
    : '';
  const previewCookbookCount = Array.isArray(orderedNodes) ? orderedNodes.length : 0;

  const handleOpenTrackPreview = useCallback(
    (track) => {
      onSelectTrack?.(track);
      setTrackPreviewOpen(true);
    },
    [onSelectTrack]
  );

  const handleCloseTrackPreview = useCallback(() => {
    setShowReviewFace(false);
    setTrackPreviewOpen(false);
    setTrackPreviewFocusNodeId(null);
    setScopeNavIndicatorLabel(null);
  }, []);

  const handleLearnNavBack = useCallback(() => {
    setScopeNavIndicatorLabel(null);
    if (showReviewFace) {
      setShowReviewFace(false);
      return;
    }
    if (trackPreviewOpen) {
      handleCloseTrackPreview();
    }
  }, [showReviewFace, trackPreviewOpen, handleCloseTrackPreview]);

  const handleLearnNavForward = useCallback(() => {
    if (!canFlipToReview || showReviewFace) return;
    setScopeNavIndicatorLabel(null);
    setShowReviewFace(true);
  }, [canFlipToReview, showReviewFace]);

  const canFrontContainerOpenReview = canFlipToReview && !showReviewFace;

  const canBackContainerCloseReview = canFlipToReview && showReviewFace;

  const reviewClickExclusionSelector =
    '.roadmap-list__scope-nav, .roadmap-list__tracks-wrap, .roadmap-list__pagination, .roadmap-list__track-card, .roadmap-list__review-menu, .roadmap-list__review-menu-btn, .roadmap-list__review-menu-back';

  const handleFrontContainerClick = useCallback(
    (e) => {
      if (!canFrontContainerOpenReview) return;
      if (e.target.closest(reviewClickExclusionSelector)) {
        return;
      }
      handleLearnNavForward();
    },
    [canFrontContainerOpenReview, handleLearnNavForward, reviewClickExclusionSelector]
  );

  const handleBackContainerClick = useCallback(
    (e) => {
      if (!canBackContainerCloseReview) return;
      if (
        e.target.closest(
          '.roadmap-list__scope-nav, .roadmap-list__review-menu-due-list, .roadmap-list__review-menu-cookbook-list, .roadmap-list__review-menu-cookbook-row, .roadmap-list__review-menu-cookbook-drawer, .roadmap-list__review-menu-footer, .roadmap-list__review-menu-continue, .roadmap-list__review-menu-back, .roadmap-list__snippet-row'
        )
      ) {
        return;
      }
      handleLearnNavBack();
    },
    [canBackContainerCloseReview, handleLearnNavBack]
  );

  const handleBackContainerKeyDown = useCallback(
    (e) => {
      if (!canBackContainerCloseReview) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleLearnNavBack();
      }
    },
    [canBackContainerCloseReview, handleLearnNavBack]
  );

  const handleFrontContainerKeyDown = useCallback(
    (e) => {
      if (!canFrontContainerOpenReview) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleLearnNavForward();
      }
    },
    [canFrontContainerOpenReview, handleLearnNavForward]
  );

  useEffect(() => {
    if (!initialOpenTrackPreview || !selectedTrack?.trackId) return;
    setTrackPreviewOpen(true);
    if (initialFocusNodeId) {
      setTrackPreviewFocusNodeId(String(initialFocusNodeId));
    }
    onInitialOpenTrackPreviewConsumed?.();
  }, [initialOpenTrackPreview, initialFocusNodeId, selectedTrack?.trackId, onInitialOpenTrackPreviewConsumed]);

  const trackPageCount = Math.max(1, Math.ceil(filteredTracks.length / TRACKS_PER_PAGE));

  useEffect(() => {
    setTrackPage(0);
  }, [language, trackQuery]);

  useEffect(() => {
    if (trackPage > trackPageCount - 1) {
      setTrackPage(0);
    }
  }, [trackPage, trackPageCount]);

  useEffect(() => {
    if (!selectedTrack?.trackId || filteredTracks.length === 0) return;
    const trackIdx = filteredTracks.findIndex((t) => t.trackId === selectedTrack.trackId);
    if (trackIdx < 0) return;
    const targetPage = Math.floor(trackIdx / TRACKS_PER_PAGE);
    setTrackPage((current) => (current === targetPage ? current : targetPage));
  }, [selectedTrack?.trackId, filteredTracks]);

  const visibleTracks = useMemo(() => {
    const start = trackPage * TRACKS_PER_PAGE;
    return filteredTracks.slice(start, start + TRACKS_PER_PAGE);
  }, [filteredTracks, trackPage]);

  const trackRangeStart =
    filteredTracks.length === 0 ? 0 : trackPage * TRACKS_PER_PAGE + 1;
  const trackRangeEnd = Math.min(
    (trackPage + 1) * TRACKS_PER_PAGE,
    filteredTracks.length
  );
  const canTrackPagePrev = trackPage > 0;
  const canTrackPageNext = trackPage < trackPageCount - 1;

  /**
   * Aggregated language-wide stats for the expandable header detail panel + the overall
   * progress bar. Iterates every track's cached nodes; tracks whose nodes haven't been
   * loaded yet are skipped (their contribution shows as 0 until RoadmapView loads them).
   *
   * Snippet totals back the header mastery stat. Topic completion snapshots are kept
   * for per-cookbook test state only; track progress uses snippet mastery.
   */
  const languageStats = useMemo(() => {
    let totalSnippets = 0;
    let masteredSnippets = 0;
    let totalTopics = 0;
    let completedTopics = 0;
    const selectedTrackId = selectedTrack?.trackId;
    const liveSelectedDone = new Set(completedNodeIds || []);
    for (const track of renderedTracks) {
      const nodes = nodesByTrackId && typeof nodesByTrackId.get === 'function'
        ? nodesByTrackId.get(track.trackId)
        : null;
      if (!Array.isArray(nodes)) continue;
      totalTopics += nodes.length;
      const trackDone =
        track.trackId === selectedTrackId
          ? liveSelectedDone
          : (completedNodesByTrackId &&
            typeof completedNodesByTrackId.get === 'function' &&
            completedNodesByTrackId.get(track.trackId)) ||
          new Set();
      for (const node of nodes) {
        if (trackDone.has(node.id)) completedTopics += 1;
        const ids = roadmapService.uniqueSnippetIdsInOrder(node.snippetIds || []);
        totalSnippets += ids.length;
        for (const id of ids) {
          if (isSnippetMastered(snippetStatsById?.[id])) masteredSnippets += 1;
        }
      }
    }
    const snippetMasteryPct =
      totalSnippets > 0 ? Math.round((masteredSnippets / totalSnippets) * 100) : 0;
    return {
      totalSnippets,
      masteredSnippets,
      totalTopics,
      completedTopics,
      snippetMasteryPct
    };
  }, [
    renderedTracks,
    nodesByTrackId,
    completedNodesByTrackId,
    snippetStatsById,
    selectedTrack?.trackId,
    completedNodeIds
  ]);

  const languageBrandColor = getLanguageBrandColor(language);

  const languageDueCount = useMemo(() => {
    if (!(dueSnippetIds instanceof Set) || dueSnippetIds.size === 0) return 0;
    const seen = new Set();
    let count = 0;
    for (const track of renderedTracks) {
      const nodes = nodesByTrackId?.get?.(track.trackId);
      if (!Array.isArray(nodes)) continue;
      for (const node of nodes) {
        for (const id of node.snippetIds || []) {
          if (!id || seen.has(id)) continue;
          seen.add(id);
          if (dueSnippetIds.has(id)) count += 1;
        }
      }
    }
    return count;
  }, [renderedTracks, nodesByTrackId, dueSnippetIds]);

  /** Due snippets in roadmap order — drives the review flip snippet list. */
  const languageDueSnippetEntries = useMemo(() => {
    if (!(dueSnippetIds instanceof Set) || dueSnippetIds.size === 0) return [];
    const entries = [];
    const seen = new Set();
    for (const track of renderedTracks) {
      const nodes = nodesByTrackId?.get?.(track.trackId);
      if (!Array.isArray(nodes)) continue;
      for (const node of nodes) {
        for (const id of node.snippetIds || []) {
          if (!id || seen.has(id)) continue;
          seen.add(id);
          if (dueSnippetIds.has(id)) {
            entries.push({ snippetId: id, track, node });
          }
        }
      }
    }
    return entries;
  }, [renderedTracks, nodesByTrackId, dueSnippetIds]);

  const trackDueCount = useMemo(
    () => computeTrackDueCount(orderedNodes, dueSnippetIds),
    [orderedNodes, dueSnippetIds]
  );

  const trackDueSnippetEntries = useMemo(
    () =>
      selectedTrack
        ? computeTrackDueSnippetEntries(selectedTrack, orderedNodes, dueSnippetIds)
        : [],
    [selectedTrack, orderedNodes, dueSnippetIds]
  );

  const isTrackReview = trackPreviewOpen && showReviewFace;
  const activeReviewDueCount = isTrackReview ? trackDueCount : languageDueCount;
  const activeReviewDueEntries = isTrackReview
    ? trackDueSnippetEntries
    : languageDueSnippetEntries;

  const activeReviewCookbookGroups = useMemo(
    () =>
      isTrackReview
        ? groupTrackDueSnippetEntries(orderedNodes, trackDueSnippetEntries)
        : groupLanguageDueSnippetEntries(
            renderedTracks,
            nodesByTrackId,
            languageDueSnippetEntries
          ),
    [
      isTrackReview,
      orderedNodes,
      trackDueSnippetEntries,
      renderedTracks,
      nodesByTrackId,
      languageDueSnippetEntries
    ]
  );

  const scopeNavDueIndicatorLabel =
    activeReviewDueCount > 0
      ? `${activeReviewDueCount} snippet${activeReviewDueCount === 1 ? '' : 's'} due`
      : 'No snippets due for review';

  const trackNavExpandedLabel =
    previewCookbookCount > 0
      ? `${previewCookbookCount} cookbook${previewCookbookCount === 1 ? '' : 's'} · ${previewTrackTitle}`
      : previewTrackTitle;

  const trackNavCompactAriaLabel =
    previewCookbookCount > 0
      ? `${previewCookbookCount} cookbook${previewCookbookCount === 1 ? '' : 's'} in ${previewTrackTitle}`
      : previewTrackTitle;

  const learnNavPage = showReviewFace ? 'review' : trackPreviewOpen ? 'track' : 'tracks';
  const canLearnNavBack = learnNavPage === 'review' || learnNavPage === 'track';
  const canLearnNavForward =
    (learnNavPage === 'tracks' || learnNavPage === 'track') && canFlipToReview;
  const showLearnScopeNav = canFlipToReview || trackPreviewOpen;

  const learnNavBackAriaLabel =
    learnNavPage === 'review'
      ? trackPreviewOpen
        ? `Back to ${previewTrackTitle}`
        : `Back to ${scopeTitle} tracks`
      : learnNavPage === 'track'
        ? `Back to ${scopeTitle} tracks`
        : 'Back';

  const learnNavForwardAriaLabel = trackPreviewOpen
    ? `Open review for ${previewTrackTitle}`
    : `Open review for ${scopeTitle}`;

  const learnNavExpandedLabel =
    learnNavPage === 'review'
      ? scopeNavDueIndicatorLabel
      : learnNavPage === 'track'
        ? trackDueCount > 0
          ? scopeNavDueIndicatorLabel
          : trackNavExpandedLabel
        : SHOW_REVIEW_HOVER_LABEL;

  const learnNavCompactAriaLabel =
    learnNavPage === 'review'
      ? scopeNavDueIndicatorLabel
      : learnNavPage === 'track'
        ? trackDueCount > 0
          ? `${trackDueCount} snippet${trackDueCount === 1 ? '' : 's'} due for review`
          : trackNavCompactAriaLabel
        : languageDueCount > 0
          ? `${languageDueCount} snippet${languageDueCount === 1 ? '' : 's'} due for review`
          : 'No snippets due for review';

  const reviewMenuBackLabel = trackPreviewOpen ? previewTrackTitle : `${scopeTitle} tracks`;

  const handleScopeContainerMouseEnter = useCallback(() => {
    setScopeNavIndicatorLabel(learnNavExpandedLabel);
  }, [learnNavExpandedLabel]);

  const handleScopeContainerMouseLeave = useCallback(() => {
    setScopeNavIndicatorLabel(null);
  }, []);

  const handleFrontContainerMouseMove = useCallback(
    (e) => {
      if (!canFrontContainerOpenReview) return;
      if (
        e.target.closest(
          '.roadmap-list__tracks-wrap, .roadmap-list__pagination, .roadmap-list__scope-nav'
        )
      ) {
        setScopeNavIndicatorLabel(null);
        return;
      }
      setScopeNavIndicatorLabel(SHOW_REVIEW_HOVER_LABEL);
    },
    [canFrontContainerOpenReview]
  );

  const handleFrontContainerMouseLeave = useCallback(() => {
    if (!canFrontContainerOpenReview) return;
    setScopeNavIndicatorLabel(null);
  }, [canFrontContainerOpenReview]);

  const handleBackContainerMouseMove = useCallback(
    (e) => {
      if (!canBackContainerCloseReview) return;
      if (
        e.target.closest(
          '.roadmap-list__review-menu-due-list, .roadmap-list__review-menu-cookbook-list, .roadmap-list__review-menu-cookbook-row, .roadmap-list__review-menu-footer, .roadmap-list__scope-nav'
        )
      ) {
        setScopeNavIndicatorLabel(null);
        return;
      }
      setScopeNavIndicatorLabel(learnNavBackAriaLabel);
    },
    [canBackContainerCloseReview, learnNavBackAriaLabel]
  );

  const handleBackContainerMouseLeave = useCallback(() => {
    if (!canBackContainerCloseReview) return;
    setScopeNavIndicatorLabel(null);
  }, [canBackContainerCloseReview]);

  const handleTrackHeaderMouseEnter = useCallback(() => {
    if (!trackPreviewOpen) return;
    setScopeNavIndicatorLabel(
      canFlipToReview ? SHOW_REVIEW_HOVER_LABEL : trackNavExpandedLabel
    );
  }, [trackPreviewOpen, canFlipToReview, trackNavExpandedLabel]);

  const handleTrackHeaderMouseLeave = useCallback(() => {
    if (!trackPreviewOpen) return;
    setScopeNavIndicatorLabel(null);
  }, [trackPreviewOpen]);

  const scopeNavActionHint =
    (canFrontContainerOpenReview && scopeNavIndicatorLabel === SHOW_REVIEW_HOVER_LABEL) ||
    (canBackContainerCloseReview && Boolean(scopeNavIndicatorLabel));

  const learnScopeNavIndicatorContent = scopeNavActionHint ? (
    <span className="roadmap-list__scope-nav-display-indicator-hint">{scopeNavIndicatorLabel}</span>
  ) : learnNavPage === 'track' ? (
    trackDueCount > 0 ? (
      <>
        <ReviewIcon />
        <span className="roadmap-list__scope-nav-display-indicator-value">
          {trackDueCount}
        </span>
        <span className="roadmap-list__scope-nav-display-indicator-label">due</span>
      </>
    ) : previewCookbookCount > 0 ? (
      <>
        <span className="roadmap-list__scope-nav-display-indicator-icon" aria-hidden>
          <CookbookIcon />
        </span>
        <span className="roadmap-list__scope-nav-display-indicator-value">
          {previewCookbookCount}
        </span>
        <span className="roadmap-list__scope-nav-display-indicator-label">
          {previewCookbookCount === 1 ? 'cookbook' : 'cookbooks'}
        </span>
      </>
    ) : (
      <span className="roadmap-list__scope-nav-display-indicator-empty">Track</span>
    )
  ) : learnNavPage === 'review' || activeReviewDueCount > 0 ? (
    <>
      <ReviewIcon />
      <span className="roadmap-list__scope-nav-display-indicator-value">
        {activeReviewDueCount}
      </span>
      <span className="roadmap-list__scope-nav-display-indicator-label">due</span>
    </>
  ) : (
    <span className="roadmap-list__scope-nav-display-indicator-empty">Review</span>
  );

  const learnScopeNav = showLearnScopeNav ? (
    <RoadmapListScopeNav
      backActive={canLearnNavBack}
      backInteractive={canLearnNavBack}
      onBackClick={handleLearnNavBack}
      backAriaLabel={learnNavBackAriaLabel}
      backEmphasized={canBackContainerCloseReview && Boolean(scopeNavIndicatorLabel)}
      forwardActive={canLearnNavForward}
      forwardInteractive={canLearnNavForward}
      onForwardClick={handleLearnNavForward}
      forwardAriaLabel={learnNavForwardAriaLabel}
      forwardEmphasized={
        canFrontContainerOpenReview && scopeNavIndicatorLabel === SHOW_REVIEW_HOVER_LABEL
      }
      indicatorHint={scopeNavActionHint}
      indicatorAriaLabel={scopeNavIndicatorLabel || learnNavCompactAriaLabel}
    >
      {learnScopeNavIndicatorContent}
    </RoadmapListScopeNav>
  ) : null;

  const tracksCookbooksMeta = (
    <div className="roadmap-list__page-header-meta">
      <span className="roadmap-list__page-subtitle-meta">
        <span className="roadmap-list__page-subtitle-meta-tracks">
          <RoadmapIcon />
          {totalTracks} {totalTracks === 1 ? 'track' : 'tracks'}
        </span>
        {languageStats.totalTopics > 0 ? (
          <>
            <span className="roadmap-list__page-subtitle-meta-sep" aria-hidden>
              ·
            </span>
            <span className="roadmap-list__page-subtitle-meta-cookbooks">
              <CookbookIcon />
              {languageStats.totalTopics}{' '}
              {languageStats.totalTopics === 1 ? 'cookbook' : 'cookbooks'}
            </span>
          </>
        ) : null}
      </span>
    </div>
  );

  return (
    <div
      className="roadmap-list"
      style={{ '--roadmap-list-language-accent': languageBrandColor }}
    >
      <div
        className={`roadmap-list__container-flip ${showReviewFace ? 'roadmap-list__container-flip--review' : ''}`.trim()}
      >
        <div className="roadmap-list__container-flip-inner">
          <div
            className={[
              `roadmap-list__container roadmap-list__container--${scopeKind} roadmap-list__container-face roadmap-list__container-face--front`,
              canFrontContainerOpenReview
                ? 'roadmap-list__container-face--review-clickable'
                : ''
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={canFrontContainerOpenReview ? handleFrontContainerClick : undefined}
            onKeyDown={canFrontContainerOpenReview ? handleFrontContainerKeyDown : undefined}
            onMouseMove={canFrontContainerOpenReview ? handleFrontContainerMouseMove : undefined}
            onMouseLeave={canFrontContainerOpenReview ? handleFrontContainerMouseLeave : undefined}
            role={canFrontContainerOpenReview ? 'button' : undefined}
            tabIndex={canFrontContainerOpenReview ? 0 : undefined}
            aria-label={canFrontContainerOpenReview ? learnNavForwardAriaLabel : undefined}
          >
            <header className="roadmap-list__page-header-wrap">
              <div className="roadmap-list__page-header">
                <div
                  className={[
                    'roadmap-list__page-header-left',
                    trackPreviewOpen || canFrontContainerOpenReview
                      ? 'roadmap-list__page-header-review-clickzone'
                      : ''
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  title={canFrontContainerOpenReview ? learnNavForwardAriaLabel : undefined}
                  onMouseEnter={trackPreviewOpen ? handleTrackHeaderMouseEnter : undefined}
                  onMouseLeave={trackPreviewOpen ? handleTrackHeaderMouseLeave : undefined}
                >
                  <div
                    className={[
                      'roadmap-list__page-header-text',
                      scopeKind === 'role' ? 'roadmap-list__page-header-text--role' : '',
                      scopeKind === 'language' ? 'roadmap-list__page-header-text--language' : '',
                      trackPreviewOpen ? 'roadmap-list__page-header-text--track-preview' : ''
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <div className="roadmap-list__page-header-title-band">
                      <div className="roadmap-list__page-header-title-band-left">
                        {trackPreviewOpen && selectedTrack ? (
                          <>
                            <RoadmapListTrackHeaderIcon />
                            <span className="roadmap-list__page-title">{previewTrackTitle}</span>
                          </>
                        ) : (
                          <>
                            <RoadmapListScopeIcon
                              scopeKind={scopeKind}
                              roleIconClass={scopeRoleIconClass}
                              language={language}
                            />
                            <span className="roadmap-list__page-title">{scopeTitle}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {!trackPreviewOpen && scopeSubtitle ? (
                      <div className="roadmap-list__page-header-promise">
                        <span className="roadmap-list__page-subtitle">{scopeSubtitle}</span>
                      </div>
                    ) : null}
                    {trackPreviewOpen && selectedTrack?.description ? (
                      <div className="roadmap-list__page-header-promise">
                        <span className="roadmap-list__page-subtitle">{selectedTrack.description}</span>
                      </div>
                    ) : null}
                    {!trackPreviewOpen && scopeKind === 'language' ? tracksCookbooksMeta : null}
                  </div>
                </div>
                <div className="roadmap-list__page-header-right">
                  <div className="roadmap-list__page-header-right-actions">
                    {learnScopeNav}
                  </div>
                  {!trackPreviewOpen && scopeKind === 'role' ? tracksCookbooksMeta : null}
                </div>
              </div>
            </header>

            {trackPreviewOpen && selectedTrack ? (
              <TrackPreviewPanel
                track={selectedTrack}
                cookbooks={orderedNodes || []}
                snippetStatsById={snippetStatsById || {}}
                dueSnippetIds={dueSnippetIds}
                isLoadingCookbooks={isLoadingNodes}
                completedNodeIds={completedNodeIds}
                currentNodeId={currentNodeId}
                nodeLastAttempts={nodeLastAttempts}
                canPreviewSnippets={
                  userCanAccessRoadmapTrack(isPremiumUser, selectedTrack.trackId, selectedTrack) ||
                  isRoadmapFreePublicTrack(selectedTrack)
                }
                onStudySnippet={onStudySnippet}
                onStudyNode={onStudyNode}
                onStartNode={onStartNode}
                onContinueNode={onContinueNode}
                onPracticeStarted={onPracticeStarted}
                loadingNodeAction={loadingNodeAction}
                initialFocusNodeId={trackPreviewFocusNodeId}
              />
            ) : (
              <>
            <div className="roadmap-list__tracks-wrap">
              <div
                className={[
                  'roadmap-list__tracks',
                  showTrackSequence ? 'roadmap-list__tracks--sequenced' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {isLoadingTracks && renderedTracks.length === 0 ? (
                  <div className="roadmap-list__placeholder">Loading tracks…</div>
                ) : renderedTracks.length === 0 ? (
                  <div className="roadmap-list__placeholder">
                    {emptyMessage || 'No tracks available for this language.'}
                  </div>
                ) : hasActiveTrackSearch && filteredTracks.length === 0 ? (
                  <div className="roadmap-list__placeholder">No tracks match your search.</div>
                ) : (
                  visibleTracks.map((track, i) => {
                    const globalIndex = trackPage * TRACKS_PER_PAGE + i;
                    const accessible =
                      userCanAccessRoadmapTrack(isPremiumUser, track.trackId, track) ||
                      isRoadmapFreePublicTrack(track);
                    return (
                      <TrackRow
                        key={track.trackId || track.id || globalIndex}
                        index={globalIndex}
                        track={track}
                        language={language}
                        isAccessible={accessible}
                        showSequence={showTrackSequence}
                        trackNodes={
                          nodesByTrackId && typeof nodesByTrackId.get === 'function'
                            ? nodesByTrackId.get(track.trackId)
                            : null
                        }
                        snippetStatsById={snippetStatsById || {}}
                        dueSnippetIds={dueSnippetIds}
                        onOpenTrackPreview={handleOpenTrackPreview}
                      />
                    );
                  })
                )}
              </div>
            </div>

            {renderedTracks.length > 0 ? (
              <nav
                className={[
                  'roadmap-list__pagination',
                  showTrackSearch ? 'roadmap-list__pagination--with-track-search' : '',
                  showRolePathControl ? 'roadmap-list__pagination--with-role-path' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-label="Track list pagination"
              >
                {showRolePathControl ? (
                  <button
                    type="button"
                    className={[
                      'roadmap-list__set-path-btn',
                      isPrimaryRolePath ? 'roadmap-list__set-path-btn--active' : '',
                      isSettingRolePath ? 'roadmap-list__set-path-btn--saving' : ''
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={onSetAsRolePath}
                    disabled={isSettingRolePath || isPrimaryRolePath}
                    aria-pressed={isPrimaryRolePath}
                    title={
                      isPrimaryRolePath
                        ? 'Your learning path'
                        : `Set ${scopeTitle} as your learning path`
                    }
                    aria-label={
                      isPrimaryRolePath
                        ? 'This role is your learning path'
                        : `Set ${scopeTitle} as your learning path`
                    }
                  >
                    {isPrimaryRolePath ? (
                      <StudyActionIcon className="roadmap-list__set-path-btn-icon" />
                    ) : null}
                  </button>
                ) : null}

                {showTrackSearch ? (
                  <>
                    <label className="roadmap-list__pagination-track-search">
                      <SearchIcon
                        className="roadmap-list__pagination-track-search-icon"
                        aria-hidden
                        focusable="false"
                      />
                      <input
                        type="search"
                        className="roadmap-list__pagination-track-search-input"
                        value={trackQuery}
                        onChange={(e) => setTrackQuery(e.target.value)}
                        placeholder="Search tracks…"
                        aria-label="Search tracks"
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </label>
                    <span className="roadmap-list__pagination-search-sep" aria-hidden />
                  </>
                ) : null}

                <div
                  className="roadmap-list__pagination-context"
                  role={footerContext ? 'status' : undefined}
                  aria-live={footerContext ? 'polite' : undefined}
                  aria-label={
                    footerContext
                      ? `Current track ${footerContext.trackNumber}, ${footerContext.trackLabel}, cookbook ${footerContext.cookbookTitle}`
                      : undefined
                  }
                >
                  {footerContext ? (
                    <>
                      <span
                        className="roadmap-list__pagination-context-track"
                        title={`${footerContext.trackNumber} · ${footerContext.trackLabel}`}
                      >
                        <span className="roadmap-list__pagination-context-track-num">
                          {footerContext.trackNumber}
                        </span>
                        <span className="roadmap-list__pagination-context-sep" aria-hidden>
                          ·
                        </span>
                        <span className="roadmap-list__pagination-context-track-name">
                          {footerContext.trackLabel}
                        </span>
                      </span>
                      <span className="roadmap-list__pagination-context-sep" aria-hidden>
                        ·
                      </span>
                      <span
                        className="roadmap-list__pagination-context-cookbook"
                        title={footerContext.cookbookTitle}
                      >
                        {footerContext.cookbookTitle}
                      </span>
                    </>
                  ) : null}
                </div>

                <div
                  className="roadmap-list__pagination-status"
                  role="status"
                  aria-live="polite"
                >
                  {trackPageCount > 1 ? (
                    <div
                      className="roadmap-list__pagination-dots"
                      role="tablist"
                      aria-label="Track page"
                    >
                      {Array.from({ length: trackPageCount }).map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          role="tab"
                          aria-selected={i === trackPage}
                          aria-label={`Go to track page ${i + 1} of ${trackPageCount}`}
                          className={`roadmap-list__pagination-dot ${i === trackPage ? 'roadmap-list__pagination-dot--active' : ''}`.trim()}
                          onClick={() => setTrackPage(i)}
                        />
                      ))}
                    </div>
                  ) : null}
                  <span
                    className="roadmap-list__pagination-count"
                    aria-label={`Tracks ${trackRangeStart} to ${trackRangeEnd} of ${filteredTracks.length}`}
                  >
                    <strong className="roadmap-list__pagination-count-current">
                      {trackRangeStart}–{trackRangeEnd}
                    </strong>
                    <span className="roadmap-list__pagination-count-separator" aria-hidden>
                      /
                    </span>
                    <span className="roadmap-list__pagination-count-total">
                      {filteredTracks.length}
                    </span>
                  </span>
                </div>

                <div className="roadmap-list__pagination-nav">
                  <button
                    type="button"
                    className="roadmap-list__pagination-button roadmap-list__pagination-button--prev"
                    onClick={() => setTrackPage((p) => Math.max(0, p - 1))}
                    disabled={!canTrackPagePrev}
                    aria-label="Previous page of tracks"
                  >
                    <NavBackIcon
                      className="roadmap-list__pagination-button-icon"
                      aria-hidden
                      focusable="false"
                    />
                  </button>
                  <button
                    type="button"
                    className="roadmap-list__pagination-button roadmap-list__pagination-button--next"
                    onClick={() => setTrackPage((p) => Math.min(trackPageCount - 1, p + 1))}
                    disabled={!canTrackPageNext}
                    aria-label="Next page of tracks"
                  >
                    <NavForwardIcon
                      className="roadmap-list__pagination-button-icon"
                      aria-hidden
                      focusable="false"
                    />
                  </button>
                </div>
              </nav>
            ) : null}
              </>
            )}
          </div>

          {canFlipToReview ? (
            <div
              className={[
                `roadmap-list__container roadmap-list__container--${scopeKind} roadmap-list__container-face roadmap-list__container-face--back`,
                canBackContainerCloseReview
                  ? 'roadmap-list__container-face--review-clickable'
                  : ''
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={canBackContainerCloseReview ? handleBackContainerClick : undefined}
              onKeyDown={canBackContainerCloseReview ? handleBackContainerKeyDown : undefined}
              onMouseMove={canBackContainerCloseReview ? handleBackContainerMouseMove : undefined}
              onMouseLeave={canBackContainerCloseReview ? handleBackContainerMouseLeave : undefined}
              onMouseEnter={showLearnScopeNav ? handleScopeContainerMouseEnter : undefined}
              role={canBackContainerCloseReview ? 'button' : undefined}
              tabIndex={canBackContainerCloseReview ? 0 : undefined}
              aria-label={canBackContainerCloseReview ? learnNavBackAriaLabel : undefined}
            >
              <header className="roadmap-list__page-header-wrap">
                <div className="roadmap-list__page-header">
                  <div
                    className={[
                      'roadmap-list__page-header-left',
                      canBackContainerCloseReview
                        ? 'roadmap-list__page-header-review-clickzone'
                        : ''
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    title={canBackContainerCloseReview ? learnNavBackAriaLabel : undefined}
                  >
                    <div
                      className={[
                        'roadmap-list__page-header-text',
                        scopeKind === 'role'
                          ? 'roadmap-list__page-header-text--role'
                          : 'roadmap-list__page-header-text--language'
                      ].join(' ')}
                    >
                      <div className="roadmap-list__page-header-title-band">
                        <div className="roadmap-list__page-header-title-band-left">
                          {isTrackReview && selectedTrack ? (
                            <>
                              <RoadmapListTrackHeaderIcon />
                              <span className="roadmap-list__page-title">{previewTrackTitle}</span>
                            </>
                          ) : (
                            <>
                              <RoadmapListScopeIcon
                                scopeKind={scopeKind}
                                roleIconClass={scopeRoleIconClass}
                                language={language}
                              />
                              <span className="roadmap-list__page-title">Review</span>
                            </>
                          )}
                        </div>
                      </div>
                      {isTrackReview ? (
                        <div className="roadmap-list__page-header-promise">
                          <span className="roadmap-list__page-subtitle">Review</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="roadmap-list__page-header-right">
                    <div className="roadmap-list__page-header-right-actions">
                      {learnScopeNav}
                    </div>
                  </div>
                </div>
              </header>
              <LearnReviewMenu
                dueSnippetEntries={activeReviewDueEntries}
                cookbookGroups={activeReviewCookbookGroups}
                snippetStatsById={snippetStatsById}
                isVisible={showReviewFace}
                continueLabel={continueStudyLabel}
                continueDisabled={continueStudyDisabled}
                onReviewDueSnippet={onReviewDueSnippet}
                onContinueStudy={onContinueStudy}
                onBackToTracks={handleLearnNavBack}
                backLabel={reviewMenuBackLabel}
                language={language}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
